const STYLE_ID = 'signal-clash-hud-theme-styles';

export function injectHudThemeStyles(): void {
  if (typeof document === 'undefined') return;
  document.getElementById(STYLE_ID)?.remove();
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = HUD_THEME_CSS;
  document.head.appendChild(style);
}

const HUD_THEME_CSS = `
:root {
  --hud-blue-core: var(--mm-blue-core, #1ea7ff);
  --hud-blue-bright: var(--mm-blue-bright, #6fdcff);
  --hud-blue-pure: var(--mm-blue-pure, #aef0ff);
  --hud-blue-glow: rgba(30, 167, 255, 0.88);
  --hud-red-core: var(--mm-red-core, #ff2a2a);
  --hud-red-bright: var(--mm-red-bright, #ff7a5a);
  --hud-red-pure: var(--mm-red-pure, #ffd6c8);
  --hud-amber: var(--mm-amber, #ffe680);
  --hud-green: var(--mm-green, #6ed788);
  --hud-ink: var(--mm-ink, #eef4ff);
  --hud-muted: var(--mm-ink-muted, #a3b8d2);
  --hud-faint: var(--mm-ink-faint, #6a7d96);
  --hud-panel: rgba(5, 9, 17, 0.88);
  --hud-panel-strong: rgba(5, 9, 17, 0.96);
  --hud-line: rgba(174, 240, 255, 0.24);
  --hud-line-strong: rgba(174, 240, 255, 0.58);
  --hud-font-display: var(--mm-font-display, 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace);
  --hud-font-ui: var(--mm-font-ui, 'Rajdhani', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif);
}

#hud,
#mobile-action-bar,
#mobile-status-strip,
#minimap,
#kill-feed,
#armed-banner,
#critical-flip-banners,
#relay-under-attack-alerts,
#reconnect-banner,
#announcer-banner,
#doctrine-picker,
#end-of-match,
#leave-match-trigger,
#leave-match-overlay,
#signalclash-intro,
#tutorial-root,
#camera-controls-hint,
#orientation-hint,
#mobile-best-on-desktop-banner,
#relay-tooltip,
#hud-tooltip-portal {
  font-family: var(--hud-font-ui);
  -webkit-font-smoothing: antialiased;
}

#hud *,
#mobile-action-bar *,
#mobile-status-strip *,
#minimap *,
#doctrine-picker *,
#end-of-match *,
#leave-match-overlay *,
#signalclash-intro *,
#tutorial-root *,
#camera-controls-hint *,
#orientation-hint *,
#mobile-best-on-desktop-banner * {
  box-sizing: border-box;
}

#hud .hud__resources,
#hud .hud-enemy,
#hud .hud-context__panel,
.build-panel,
#mobile-action-bar,
#mobile-status-strip,
#minimap,
.dp__card,
.eom__card,
.leave-match-card,
.signalclash-intro__card,
.tutorial-card,
.cch__card,
#orientation-hint,
#mobile-best-on-desktop-banner,
#relay-tooltip,
#hud-tooltip-portal,
.reconnect-banner__label,
.critical-flip-banner,
.relay-under-attack-banner,
.kill-feed__row,
#armed-banner {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.075), transparent 34%, rgba(0,0,0,0.34) 100%),
    radial-gradient(ellipse 120% 120% at 0% 50%, rgba(30, 167, 255, 0.15), transparent 58%),
    radial-gradient(ellipse 120% 120% at 100% 50%, rgba(255, 42, 42, 0.11), transparent 58%),
    linear-gradient(180deg, rgba(9, 18, 31, 0.9), rgba(4, 7, 14, 0.95));
  border: 1px solid var(--hud-line);
  border-radius: 0;
  box-sizing: border-box;
  color: var(--hud-ink);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.05) inset,
    0 0 28px rgba(30,167,255,0.10) inset,
    0 14px 38px rgba(0,0,0,0.64);
  backdrop-filter: blur(8px);
}

#hud .hud__resources,
#hud .hud-context__panel,
.build-panel,
#mobile-action-bar,
#mobile-status-strip,
#minimap,
.dp__card,
.eom__card,
.leave-match-card,
.signalclash-intro__card,
.tutorial-card,
.cch__card,
#orientation-hint,
#mobile-best-on-desktop-banner {
  clip-path: polygon(14px 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%, 0 14px);
}

#hud .hud-enemy {
  clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px));
  border-color: rgba(255, 122, 90, 0.26);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.05) inset,
    0 0 26px rgba(255,42,42,0.12) inset,
    0 14px 38px rgba(0,0,0,0.64);
}

#hud .hud__resources {
  top: 14px;
  left: 14px;
  min-width: 218px;
  gap: 5px;
  padding: 11px 16px 12px;
}

#hud .hud-stat {
  gap: 18px;
}

#hud .hud-stat__label,
#hud .hud-enemy__title,
#hud .hud-context__title,
#hud .hud-context__build-title,
.dp__title,
.eom__banner,
.tutorial-card__chip,
.cch__title,
.mobile-best-banner__title {
  font-family: var(--hud-font-display);
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

#hud .hud-stat__label {
  color: var(--hud-blue-bright);
  font-size: 10px;
  opacity: 0.92;
  text-shadow: 0 0 7px rgba(111, 220, 255, 0.45);
}

#hud .hud-stat__value {
  color: var(--hud-muted);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

#hud .hud-stat__value.hud-stat__primary {
  color: #ffffff;
  font-family: var(--hud-font-display);
  font-size: 20px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-shadow:
    0 0 8px rgba(255,255,255,0.45),
    0 0 18px rgba(111,220,255,0.45);
}

#hud .hud-stat__value[data-at-cap="true"],
#hud .hud__timer[data-urgent="true"] {
  color: var(--hud-red-pure);
  text-shadow:
    0 0 8px rgba(255,122,90,0.7),
    0 0 24px rgba(255,42,42,0.52);
}

#hud .hud-signal-bar,
#hud .hud-context__hp-bar,
#hud .hud-context__queue-bar,
.mss__hp-bar {
  background: rgba(3, 8, 15, 0.92);
  border: 1px solid rgba(174,240,255,0.18);
  border-radius: 0;
  box-shadow: inset 0 0 8px rgba(0,0,0,0.72);
}

#hud .hud-signal-bar__fill,
#hud .hud-context__queue-bar-fill,
#hud .hud-context__cd-fill,
#hud .hud-context__cmd-cd {
  background: linear-gradient(90deg, var(--hud-blue-core), var(--hud-blue-pure));
  box-shadow: 0 0 10px rgba(111,220,255,0.55);
}

#hud .hud-context__hp-fill,
.mss__hp-fill {
  background: linear-gradient(90deg, #43d878, var(--hud-green));
  box-shadow: 0 0 8px rgba(110,215,136,0.44);
}

#hud .hud-context__hp-fill[data-low="true"],
.mss__hp-fill[data-low="true"] {
  background: linear-gradient(90deg, var(--hud-red-core), var(--hud-red-bright));
  box-shadow: 0 0 10px rgba(255,42,42,0.5);
}

#hud .hud__timer {
  top: 14px;
  min-width: 164px;
  padding: 9px 32px;
  background:
    linear-gradient(90deg, rgba(30,167,255,0.22), transparent 28%, transparent 72%, rgba(255,42,42,0.18)),
    linear-gradient(180deg, rgba(16,27,43,0.92), rgba(4,7,14,0.94));
  border-color: rgba(174,240,255,0.42);
  color: #ffffff;
  font-family: var(--hud-font-display);
  font-size: 27px;
  font-weight: 900;
  letter-spacing: 0.14em;
  clip-path: polygon(18px 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 18px 100%, 0 50%);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.08) inset,
    -14px 0 30px rgba(30,167,255,0.22) inset,
    14px 0 30px rgba(255,42,42,0.13) inset,
    0 0 26px rgba(111,220,255,0.28);
  overflow: hidden;
}

#hud .hud__timer::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 18%, rgba(255,255,255,0.34) 43%, rgba(174,240,255,0.22) 50%, transparent 70%);
  transform: translateX(-135%);
  animation: hud-theme-shine 4.8s ease-in-out infinite;
  pointer-events: none;
}

#hud .hud__timer::after {
  left: 24px;
  right: 24px;
  bottom: 5px;
  background: linear-gradient(90deg, transparent, var(--hud-blue-pure), transparent);
  opacity: 0.72;
}

#hud .hud__timer[data-urgent="true"] {
  border-color: rgba(255, 122, 90, 0.72);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.08) inset,
    0 0 30px rgba(255,42,42,0.28) inset,
    0 0 28px rgba(255,42,42,0.42);
}

#hud .hud__banner,
#hud .hud__overtime,
#armed-banner,
.critical-flip-banner,
.relay-under-attack-banner,
.reconnect-banner__label {
  font-family: var(--hud-font-display);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  clip-path: polygon(10px 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0 50%);
}

#hud .hud__banner {
  top: 72px;
  border-color: rgba(255,230,128,0.72);
  background: linear-gradient(90deg, rgba(255,230,128,0.16), rgba(5,9,17,0.9));
  color: var(--hud-amber);
  box-shadow: 0 0 18px rgba(255,230,128,0.2);
}

#hud .hud__overtime,
.critical-flip-banner--danger,
.relay-under-attack-banner {
  border-color: rgba(255,122,90,0.72);
  color: var(--hud-red-pure);
}

#hud .hud-enemy {
  top: 14px;
  right: 14px;
  padding: 10px 14px 11px;
}

#hud .hud-enemy__title {
  color: var(--hud-red-bright);
  font-size: 10px;
  text-shadow: 0 0 8px rgba(255,42,42,0.42);
}

#hud .hud-surge,
#hud .hud-doctrine,
.mab__hotkey {
  border-radius: 0;
  font-family: var(--hud-font-display);
  letter-spacing: 0.1em;
  clip-path: polygon(5px 0, 100% 0, 100% calc(100% - 5px), calc(100% - 5px) 100%, 0 100%, 0 5px);
}

#hud .hud-surge {
  background: rgba(111, 220, 255, 0.12);
  border-color: rgba(111, 220, 255, 0.46);
  color: var(--hud-blue-pure);
}

#hud .hud-doctrine {
  background: rgba(255, 230, 128, 0.12);
  border-color: rgba(255, 230, 128, 0.45);
  color: var(--hud-amber);
}

#hud .hud-doctrine[data-doctrine="saboteur"] {
  background: rgba(255, 42, 42, 0.12);
  border-color: rgba(255, 122, 90, 0.55);
  color: var(--hud-red-pure);
}

#hud .hud-doctrine[data-doctrine="bulwark"] {
  background: rgba(111, 220, 255, 0.12);
  border-color: rgba(111, 220, 255, 0.45);
  color: var(--hud-blue-pure);
}

#hud .hud__context {
  right: 18px;
  bottom: 18px;
  font-family: var(--hud-font-ui);
}

#hud .hud-context__empty,
#hud .hud-context__select-all {
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  background: rgba(5,9,17,0.88);
  border: 1px solid var(--hud-line);
  border-radius: 0;
}

#hud .hud-context__panel {
  min-width: 294px;
  padding: 13px 16px 15px;
  border-color: rgba(174,240,255,0.25);
}

#hud .hud-context__panel--squad {
  border-color: rgba(111,220,255,0.42);
  border-left: 0;
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.05) inset,
    -22px 0 36px rgba(30,167,255,0.12) inset,
    0 14px 38px rgba(0,0,0,0.64);
}

#hud .hud-context__panel--core {
  border-color: rgba(255,230,128,0.42);
  border-left: 0;
  background:
    linear-gradient(180deg, rgba(255,255,255,0.06), transparent 34%, rgba(0,0,0,0.34) 100%),
    radial-gradient(ellipse 110% 110% at 0% 45%, rgba(255,230,128,0.13), transparent 58%),
    radial-gradient(ellipse 110% 110% at 100% 50%, rgba(111,220,255,0.08), transparent 58%),
    linear-gradient(180deg, rgba(17,18,25,0.92), rgba(6,7,12,0.96));
}

#hud .hud-context__panel::after {
  top: 9px;
  right: 15px;
  font-family: var(--hud-font-display);
  letter-spacing: 0.16em;
  opacity: 0.78;
}

#hud .hud-context__panel--squad::after {
  color: var(--hud-blue-bright);
}

#hud .hud-context__panel--core::after {
  color: var(--hud-amber);
}

#hud .hud-context__title {
  color: var(--hud-blue-pure);
  font-size: 12px;
  text-shadow: 0 0 8px rgba(111,220,255,0.42);
}

#hud .hud-context__panel--core .hud-context__title {
  color: var(--hud-amber);
  text-shadow: 0 0 8px rgba(255,230,128,0.38);
}

#hud .hud-context__portrait,
.mss__portrait {
  background:
    linear-gradient(180deg, rgba(255,255,255,0.09), transparent 48%),
    linear-gradient(135deg, rgba(30,167,255,0.18), rgba(5,9,17,0.95));
  border-color: rgba(174,240,255,0.35);
  border-radius: 0;
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 14px rgba(111,220,255,0.18);
}

#hud .hud-context__tagline,
#hud .hud-context__stats,
#hud .hud-context__queue,
#hud .hud-context__btn-meta,
#hud .hud-context__build-hint,
.mss__core-queue,
.mab__meta,
.dp__subtitle,
.dp__btn-desc,
.eom__detail,
.tutorial-card__body,
.cch__list li,
.mobile-best-banner__body {
  color: var(--hud-muted);
}

#hud .hud-context__commands,
#hud .hud-context__buttons,
#hud .hud-context__build-row {
  gap: 7px;
}

#hud .hud-context__cmd,
#hud .hud-context__btn,
#hud .hud-context__build-btn,
#hud .hud-context__select-all,
.build-panel__btn,
.mab__btn,
.mss__select-all,
.dp__btn,
.eom__btn,
.leave-match-btn,
.signalclash-intro__cta,
.armed-banner__cancel,
.orient-hint__btn,
.mobile-best-banner__close,
.tutorial-card__skip,
.cch__close {
  position: relative;
  overflow: hidden;
  border-radius: 0;
  font-family: var(--hud-font-display);
  letter-spacing: 0.09em;
  text-transform: uppercase;
  transition:
    transform 110ms ease,
    border-color 140ms ease,
    color 140ms ease,
    background 140ms ease,
    box-shadow 140ms ease;
}

#hud .hud-context__cmd,
#hud .hud-context__btn,
#hud .hud-context__build-btn,
.build-panel__btn,
.mab__btn,
.mss__select-all,
.dp__btn,
.eom__btn,
.leave-match-btn,
.signalclash-intro__cta,
.armed-banner__cancel,
.orient-hint__btn,
.mobile-best-banner__close {
  background:
    linear-gradient(90deg, rgba(30,167,255,0.10), transparent 42%, rgba(255,42,42,0.06)),
    linear-gradient(180deg, rgba(18,31,49,0.88), rgba(5,9,17,0.95));
  border: 1px solid rgba(174,240,255,0.25);
  color: var(--hud-ink);
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
}

#hud .hud-context__cmd::before,
#hud .hud-context__btn::before,
#hud .hud-context__build-btn::before,
.build-panel__btn::before,
.mab__btn::before,
.mss__select-all::before,
.dp__btn::before,
.eom__btn::before,
.leave-match-btn::before,
.signalclash-intro__cta::before,
.armed-banner__cancel::before,
.orient-hint__btn::before,
.mobile-best-banner__close::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 18%, rgba(255,255,255,0.18) 48%, transparent 68%);
  transform: translateX(-130%);
  opacity: 0;
  pointer-events: none;
}

#hud .hud-context__cmd:hover:not(:disabled),
#hud .hud-context__btn:hover:not(:disabled),
#hud .hud-context__build-btn:hover:not(:disabled),
.build-panel__btn:hover:not(:disabled),
.mab__btn:hover:not(:disabled),
.mss__select-all:hover,
.dp__btn:hover,
.eom__btn:hover,
.leave-match-btn:hover,
.signalclash-intro__cta:hover,
.armed-banner__cancel:hover,
.orient-hint__btn:hover,
.mobile-best-banner__close:hover {
  border-color: var(--hud-blue-bright);
  color: var(--hud-blue-pure);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 18px rgba(111,220,255,0.28);
}

#hud .hud-context__cmd:hover:not(:disabled)::before,
#hud .hud-context__btn:hover:not(:disabled)::before,
#hud .hud-context__build-btn:hover:not(:disabled)::before,
.build-panel__btn:hover:not(:disabled)::before,
.mab__btn:hover:not(:disabled)::before,
.mss__select-all:hover::before,
.dp__btn:hover::before,
.eom__btn:hover::before,
.leave-match-btn:hover::before,
.signalclash-intro__cta:hover::before,
.armed-banner__cancel:hover::before,
.orient-hint__btn:hover::before,
.mobile-best-banner__close:hover::before {
  opacity: 1;
  animation: hud-theme-button-shine 760ms ease-out;
}

#hud .hud-context__cmd:active:not(:disabled),
#hud .hud-context__btn:active:not(:disabled),
#hud .hud-context__build-btn:active:not(:disabled),
.build-panel__btn:active:not(:disabled),
.mab__btn:active:not(:disabled),
.mss__select-all:active,
.dp__btn:active,
.eom__btn:active,
.leave-match-btn:active,
.signalclash-intro__cta:active,
.armed-banner__cancel:active,
.orient-hint__btn:active,
.mobile-best-banner__close:active {
  transform: scale(0.97);
}

#hud .hud-context__cmd:disabled,
#hud .hud-context__btn:disabled,
#hud .hud-context__build-btn:disabled,
.build-panel__btn:disabled,
.mab__btn:disabled {
  opacity: 0.42;
  filter: grayscale(0.25);
}

#hud .hud-context__cmd[data-armed="true"],
#hud .hud-context__build-btn[data-armed="true"],
#hud .hud-context__select-all,
.build-panel__btn[data-armed="true"],
.mab__btn[data-armed="true"],
.mss__select-all,
.eom__btn--primary {
  border-color: rgba(255,230,128,0.7);
  color: #ffffff;
  background:
    linear-gradient(90deg, rgba(30,167,255,0.16), transparent 44%, rgba(255,42,42,0.13)),
    linear-gradient(180deg, rgba(47,48,39,0.9), rgba(13,12,9,0.96));
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.08) inset,
    0 0 18px rgba(255,230,128,0.28);
}

#hud .hud-context__cmd[data-ready="true"],
.mab__btn--ability[data-ready="true"] {
  border-color: rgba(110,215,136,0.75);
  color: #c8ffd5;
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 16px rgba(110,215,136,0.42);
}

#hud .hud-context__cmd-key,
.mab__hotkey {
  background: rgba(5,9,17,0.86);
  border-color: rgba(255,230,128,0.6);
  color: var(--hud-amber);
}

#hud .hud-context__build-section {
  border-top-color: rgba(174,240,255,0.16);
}

.minimap {
  width: 188px;
  height: 188px;
  padding: 5px;
  background:
    linear-gradient(135deg, rgba(30,167,255,0.18), transparent 42%, rgba(255,42,42,0.12)),
    linear-gradient(180deg, rgba(8,15,27,0.92), rgba(3,6,12,0.96));
  border-color: rgba(174,240,255,0.32);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.05) inset,
    0 0 22px rgba(111,220,255,0.16),
    0 14px 36px rgba(0,0,0,0.66);
}

.minimap::before,
.minimap::after {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  pointer-events: none;
}

.minimap::before {
  top: 5px;
  left: 5px;
  border-top: 1px solid var(--hud-blue-bright);
  border-left: 1px solid var(--hud-blue-bright);
  box-shadow: -1px -1px 8px rgba(111,220,255,0.28);
}

.minimap::after {
  right: 5px;
  bottom: 5px;
  border-right: 1px solid var(--hud-red-bright);
  border-bottom: 1px solid var(--hud-red-bright);
  box-shadow: 1px 1px 8px rgba(255,122,90,0.25);
}

.minimap__canvas {
  border-radius: 0;
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  box-shadow: inset 0 0 0 1px rgba(174,240,255,0.18);
}

#mobile-action-bar {
  bottom: 10px;
  gap: 7px;
  padding: 7px 9px;
}

.mab__btn {
  min-width: 58px;
  min-height: 54px;
  color: var(--hud-ink);
}

.mab__icon {
  color: var(--hud-blue-bright);
  text-shadow: 0 0 10px rgba(111,220,255,0.42);
}

.mab__btn--build .mab__icon,
.mab__btn--ability .mab__label {
  color: var(--hud-amber);
}

.mab__btn--build[data-armed-kind="turret"] {
  border-color: rgba(255,122,90,0.7);
  color: var(--hud-red-pure);
  box-shadow: 0 0 18px rgba(255,42,42,0.3);
}

#mobile-status-strip {
  padding: 7px 11px;
  border-color: rgba(174,240,255,0.3);
}

.mss__name,
.mss__core-title {
  font-family: var(--hud-font-display);
  color: var(--hud-blue-pure);
}

.build-panel {
  padding: 11px 15px;
}

.build-panel__title,
.build-panel__name,
.build-panel__hint,
.dp__btn-name,
.eom__btn,
.leave-match-btn {
  font-family: var(--hud-font-display);
  text-transform: uppercase;
}

.build-panel__hint {
  color: var(--hud-amber);
}

#kill-feed {
  top: 72px;
}

.kill-feed__row {
  padding: 5px 11px;
  border-left: 0;
  border-color: currentColor;
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
  font-family: var(--hud-font-display);
  font-size: 11px;
  text-shadow: 0 0 6px currentColor;
}

.kill-feed__row--ally {
  color: var(--hud-blue-bright);
}

.kill-feed__row--enemy {
  color: var(--hud-red-bright);
}

#armed-banner {
  top: 60px;
  padding: 8px 15px;
  border-color: rgba(255,230,128,0.72);
  color: var(--hud-amber);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.05) inset,
    0 0 18px rgba(255,230,128,0.24),
    0 12px 32px rgba(0,0,0,0.62);
}

#armed-banner[data-mode="build"] {
  border-color: rgba(111,220,255,0.76);
  color: var(--hud-blue-pure);
}

#armed-banner[data-mode="cut-link"] {
  border-color: rgba(255,122,90,0.76);
  color: var(--hud-red-pure);
}

.armed-banner__cancel {
  min-height: 32px;
  padding: 6px 13px;
}

#critical-flip-banners {
  top: 62px;
}

.critical-flip-banner,
.relay-under-attack-banner {
  padding: 8px 16px;
  background:
    linear-gradient(90deg, rgba(255,42,42,0.15), rgba(5,9,17,0.92), rgba(30,167,255,0.08));
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.05) inset,
    0 0 16px currentColor,
    0 10px 28px rgba(0,0,0,0.6);
}

.critical-flip-banner--recovery,
.critical-flip-banner--success {
  color: var(--hud-green);
}

.critical-flip-banner--warning,
.critical-flip-banner--caution {
  color: var(--hud-amber);
}

.critical-flip-banner--storm {
  color: #df9cff;
}

#relay-under-attack-alerts {
  top: 112px;
}

.reconnect-banner__label {
  padding: 10px 20px;
  border-color: rgba(255,122,90,0.68);
  color: var(--hud-red-pure);
}

.announcer-banner {
  font-family: var(--hud-font-display);
  color: #ffffff;
  text-shadow:
    0 1px 0 rgba(255,255,255,0.22),
    0 3px 0 rgba(0,0,0,0.72);
  filter: none;
}

.announcer-banner::before {
  content: '';
  position: absolute;
  inset: 18% -8%;
  z-index: -1;
  background:
    linear-gradient(90deg, transparent, rgba(30,167,255,0.10) 20%, rgba(255,255,255,0.055) 50%, rgba(255,42,42,0.09) 80%, transparent),
    linear-gradient(180deg, transparent, rgba(5,9,17,0.28), transparent);
  clip-path: polygon(20px 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 20px 100%, 0 50%);
  filter: none;
  opacity: 0.72;
}

.announcer-banner__label {
  filter: none;
}

.announcer-banner--countdown {
  color: var(--hud-blue-pure);
}

.announcer-banner--fight,
.announcer-banner--victory,
.announcer-banner--double {
  color: var(--hud-amber);
}

.announcer-banner--first-blood,
.announcer-banner--defeat {
  color: var(--hud-red-bright);
}

#doctrine-picker {
  background:
    radial-gradient(ellipse at 35% 30%, rgba(30,167,255,0.18), transparent 42%),
    radial-gradient(ellipse at 70% 60%, rgba(255,42,42,0.14), transparent 44%),
    rgba(0, 0, 0, 0.66);
}

.dp__card {
  min-width: min(720px, calc(100vw - 32px));
}

.dp__title {
  color: #ffffff;
  font-size: 19px;
  text-shadow: 0 0 18px rgba(111,220,255,0.44);
}

.dp__countdown {
  color: var(--hud-red-bright);
  text-shadow: 0 0 10px rgba(255,42,42,0.46);
}

.dp__btn {
  min-height: 114px;
  align-items: flex-start;
}

.dp__btn[data-doctrine="vanguard"] {
  border-color: rgba(255,230,128,0.36);
}

.dp__btn[data-doctrine="saboteur"] {
  border-color: rgba(255,122,90,0.36);
}

.dp__btn[data-doctrine="bulwark"] {
  border-color: rgba(111,220,255,0.36);
}

#end-of-match {
  background:
    radial-gradient(ellipse at 35% 25%, rgba(30,167,255,0.18), transparent 44%),
    radial-gradient(ellipse at 72% 65%, rgba(255,42,42,0.15), transparent 44%),
    rgba(0, 0, 0, 0.72);
}

.eom__card {
  border-color: rgba(174,240,255,0.28);
}

.eom__banner {
  color: var(--hud-red-bright);
  text-shadow:
    0 0 10px rgba(255,122,90,0.7),
    0 0 34px rgba(255,42,42,0.36);
}

.eom__banner[data-win="true"] {
  color: var(--hud-blue-pure);
  text-shadow:
    0 0 10px rgba(111,220,255,0.76),
    0 0 34px rgba(30,167,255,0.42);
}

.eom__mvp-row,
.eom__spark-svg,
.eom__stats-table {
  border-radius: 0;
  border-color: rgba(174,240,255,0.18);
}

.eom__mvp-row {
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
}

#leave-match-trigger {
  top: 12px;
  right: 14px;
  min-height: 30px;
  padding: 5px 13px;
  border-radius: 0;
  clip-path: polygon(7px 0, 100% 0, 100% calc(100% - 7px), calc(100% - 7px) 100%, 0 100%, 0 7px);
  font-family: var(--hud-font-display);
  color: var(--hud-blue-bright);
}

#leave-match-overlay {
  background:
    radial-gradient(ellipse at 50% 30%, rgba(30,167,255,0.18), transparent 42%),
    rgba(0, 0, 0, 0.74);
}

.signalclash-intro__card,
.leave-match-card {
  border-color: rgba(174,240,255,0.3);
  clip-path: polygon(18px 0, 100% 0, 100% calc(100% - 18px), calc(100% - 18px) 100%, 0 100%, 0 18px);
}

.signalclash-intro__title,
.leave-match-title {
  color: #ffffff;
  font-family: var(--hud-font-display);
  text-shadow: 0 0 16px rgba(111,220,255,0.42);
}

.leave-match-btn--leave {
  border-color: rgba(255,122,90,0.68);
  color: var(--hud-red-pure);
  background:
    linear-gradient(90deg, rgba(255,42,42,0.18), transparent 58%),
    linear-gradient(180deg, rgba(38,18,20,0.9), rgba(9,6,9,0.96));
}

.tutorial-card,
.signalclash-intro__card,
.cch__card,
#orientation-hint,
#mobile-best-on-desktop-banner,
#relay-tooltip,
#hud-tooltip-portal {
  border-color: rgba(174,240,255,0.3);
}

.signalclash-intro__chip,
.tutorial-card__chip {
  background: rgba(111,220,255,0.13);
  color: var(--hud-blue-pure);
  border: 1px solid rgba(111,220,255,0.28);
  border-radius: 0;
}

.tutorial-card__title,
.signalclash-intro__item-title,
.cch__title {
  color: var(--hud-blue-pure);
}

#orientation-hint,
#mobile-best-on-desktop-banner {
  z-index: 60;
}

.orient-hint__btn,
.mobile-best-banner__close {
  min-height: 34px;
}

#relay-tooltip,
#hud-tooltip-portal {
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
}

.relay-tooltip__title--you {
  color: var(--hud-blue-bright);
}

.relay-tooltip__title--enemy {
  color: var(--hud-red-bright);
}

@keyframes hud-theme-shine {
  0%, 18% { transform: translateX(-135%); opacity: 0; }
  32% { opacity: 0.86; }
  52%, 100% { transform: translateX(135%); opacity: 0; }
}

@keyframes hud-theme-button-shine {
  0% { transform: translateX(-130%); }
  100% { transform: translateX(130%); }
}

@media (max-width: 820px), (pointer: coarse) {
  #hud .hud__resources {
    top: 6px;
    left: 6px;
    min-width: 0;
    padding: 6px 8px;
    clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
  }

  #hud .hud-enemy {
    top: 6px;
    right: 6px;
    padding: 5px 8px;
    clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px));
  }

  #hud .hud__timer {
    top: 4px;
    min-width: 112px;
    padding: 4px 13px;
    font-size: 18px;
    clip-path: polygon(11px 0, calc(100% - 11px) 0, 100% 50%, calc(100% - 11px) 100%, 11px 100%, 0 50%);
  }

  #hud .hud__banner,
  #hud .hud__overtime {
    top: 39px;
    font-size: 10px;
  }

  .minimap {
    width: 132px;
    height: 132px;
    padding: 4px;
  }

  #mobile-action-bar {
    padding: 5px 7px;
    gap: 5px;
  }

  .mab__btn {
    min-height: 46px;
  }

  #mobile-status-strip {
    padding: 5px 9px;
  }

  #armed-banner {
    top: auto;
  }

  .announcer-banner::before {
    inset: 15% -5%;
  }

  .dp__card,
  .eom__card,
  .signalclash-intro__card,
  .leave-match-card {
    clip-path: polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px);
  }
}

@media (max-width: 460px) {
  .minimap {
    width: 104px;
    height: 104px;
  }

  #mobile-status-strip {
    max-width: calc(100vw - 142px);
  }
}

@media (orientation: landscape) and (max-height: 480px) and (pointer: coarse) {
  .minimap {
    width: 90px;
    height: 90px;
  }

  #hud .hud__timer {
    font-size: 14px;
    padding: 3px 11px;
  }

  .mab__btn {
    min-height: 40px;
  }
}

@media (prefers-reduced-motion: reduce) {
  #hud .hud__timer::before,
  #hud .hud-context__cmd:hover:not(:disabled)::before,
  #hud .hud-context__btn:hover:not(:disabled)::before,
  #hud .hud-context__build-btn:hover:not(:disabled)::before,
  .build-panel__btn:hover:not(:disabled)::before,
  .mab__btn:hover:not(:disabled)::before,
  .mss__select-all:hover::before,
  .dp__btn:hover::before,
  .eom__btn:hover::before,
  .leave-match-btn:hover::before,
  .signalclash-intro__cta:hover::before,
  .armed-banner__cancel:hover::before,
  .orient-hint__btn:hover::before,
  .mobile-best-banner__close:hover::before {
    animation: none;
  }
}
`;
